import React, { useEffect, useMemo, useState } from 'react';
import './ProgressiveDisclosure.css';

const TabbedInterface = ({
  tabs = [],
  activeTab,
  defaultTab,
  onTabChange,
  lazyLoad = true,
  syncHash = false,
  className = '',
  renderPanel = true,
}) => {
  const firstTab = tabs[0]?.id;
  const initialTab = activeTab || defaultTab || firstTab;
  const [internalTab, setInternalTab] = useState(initialTab);
  const [loadedTabs, setLoadedTabs] = useState(() => new Set(initialTab ? [initialTab] : []));
  const selectedTab = activeTab || internalTab;

  useEffect(() => {
    if (!syncHash || typeof window === 'undefined') return;
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && tabs.some((tab) => tab.id === hash)) {
      setInternalTab(hash);
      onTabChange?.(hash);
    }
  }, [onTabChange, syncHash, tabs]);

  useEffect(() => {
    if (selectedTab) {
      setLoadedTabs((previous) => new Set(previous).add(selectedTab));
    }
  }, [selectedTab]);

  const selected = useMemo(
    () => tabs.find((tab) => tab.id === selectedTab) || tabs[0],
    [selectedTab, tabs]
  );

  const selectTab = (tabId) => {
    setInternalTab(tabId);
    if (syncHash && typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${tabId}`);
    }
    onTabChange?.(tabId);
  };

  const handleKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const lastIndex = tabs.length - 1;
    let nextIndex = index;
    if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    selectTab(tabs[nextIndex].id);
  };

  const renderContent = (tab) => {
    if (!tab) return null;
    if (typeof tab.content === 'function') {
      return tab.content();
    }
    return tab.content || null;
  };

  return (
    <div className={`tabbed-interface ${className}`}>
      <div className="tabbed-interface__list" role="tablist">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            id={`${tab.id}-tab`}
            role="tab"
            aria-selected={selected?.id === tab.id}
            aria-controls={`${tab.id}-panel`}
            className={`tabbed-interface__tab ${tab.className || ''}`}
            onClick={() => selectTab(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span>{tab.label || tab.display}</span>
            {tab.badge !== undefined && <span className="tabbed-interface__badge">{tab.badge}</span>}
          </button>
        ))}
      </div>
      {renderPanel && selected && (
        <div id={`${selected.id}-panel`} role="tabpanel" aria-labelledby={`${selected.id}-tab`} className="tabbed-interface__panel">
          {!lazyLoad || loadedTabs.has(selected.id) ? renderContent(selected) : null}
        </div>
      )}
    </div>
  );
};

export default TabbedInterface;
